/* =====================================================================
   ap-term.js — Kennungen im Fließtext erklären
   ---------------------------------------------------------------------
   Zwei Teile:
     1. <ap-term> — ein Toggletip (Klick, kein Hover). Gebaut nach dem im Haus
        bewährten Muster: echter <button>, aria-expanded/aria-controls, ESC
        schließt und gibt den Fokus zurück, Außenklick schließt.
     2. Eine Erkennung, die in FREIGEGEBENEN Behältern die Kennungen des
        Glossars findet und die ERSTE Fundstelle je Begriff in ein <ap-term>
        verwandelt.

   DIE REGELN, DIE DEN UNTERSCHIED MACHEN
   - NIE IN ÜBERSCHRIFTEN. h1–h6 bleiben unangetastet — eine Überschrift ist
     eine Aussage, kein Nachschlagewerk, und ein Knopf darin zerschießt den
     Umbruch. Ebenso tabu: a, button, label, code, pre, abbr und alles, was
     schon ein ap-term ist (kein Knopf im Knopf).
   - NUR DIE ERSTE FUNDSTELLE je Begriff und Seite. Ein Text, der "XL" zwölfmal
     nennt, bekommt einen Tooltip, nicht zwölf.
   - GANZE WÖRTER, GROSS-/KLEINSCHREIBUNG ZÄHLT. "MO" darf nicht in "Montage"
     treffen und "ST" nicht in "Stahl". Codes sind gedruckte Kennungen, sie
     stehen in Großbuchstaben.
   - KEINE EINZELZEICHEN. "*", "J", "B" wären im Fließtext reines Rauschen.
   - DER TEXT BLEIBT UNVERÄNDERT. Es wird nur umschlossen, nie ersetzt.

   Ohne JavaScript passiert nichts — der Text steht vollständig da. Progressive
   Enhancement, wie im ganzen Theme.
   ===================================================================== */
(() => {
  if (window.__apTermLoaded) return;
  window.__apTermLoaded = true;

  /* ---- 1. Das Element -------------------------------------------------- */
  class ApTerm extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('.ap-term__btn');
      this.pop = this.querySelector('.ap-term__pop');
      if (!this.btn || !this.pop) return;
      this.btn.addEventListener('click', () => this.toggle());
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.open) {
          this.close();
          this.btn.focus();
        }
      });
      // Fokus verlässt das Element (Tab weiter) -> schließen. relatedTarget
      // prüfen, sonst schließt es auch beim Sprung auf den Link im Panel.
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.close();
      });
      this._outside = (e) => {
        if (this.open && !this.contains(e.target)) this.close();
      };
    }

    disconnectedCallback() {
      if (this._outside) document.removeEventListener('click', this._outside);
    }

    get open() {
      return this.btn && this.btn.getAttribute('aria-expanded') === 'true';
    }

    toggle() {
      if (this.open) this.close();
      else this.show();
    }

    show() {
      // Immer nur einer offen — sonst überlagern sich zwei Panels im Fließtext.
      document.querySelectorAll('ap-term').forEach((t) => {
        if (t !== this && t.close) t.close();
      });
      this.btn.setAttribute('aria-expanded', 'true');
      this.pop.hidden = false;
      document.addEventListener('click', this._outside);
      // Am Rand des Fensters nach innen kippen.
      this.pop.classList.remove('ap-term__pop--left');
      const r = this.pop.getBoundingClientRect();
      if (r.right > window.innerWidth - 8) this.pop.classList.add('ap-term__pop--left');
    }

    close() {
      if (!this.btn) return;
      this.btn.setAttribute('aria-expanded', 'false');
      this.pop.hidden = true;
      document.removeEventListener('click', this._outside);
    }
  }
  if (!customElements.get('ap-term')) customElements.define('ap-term', ApTerm);

  /* ---- 2. Die Erkennung ------------------------------------------------ */
  /* PHASE D TEIL 2 (P1 „skripte", plan.md b1 M3, Probe PD-9). Vorher je Behaelter UND je Code (~150) ein TreeWalker
     beim Auswerten der Datei (Profil M0: PDP 224 + 148 ms). Jetzt: (1) EIN Durchgang je Behaelter, dieselbe
     Reihenfolge, Fundstellen und Ids wie bisher; (2) Start bei 200 px Annaeherung bzw. im Leerlauf, Bloecke <= 8 ms;
     (3) Knoepfe nur ausserhalb des Bildes (ein Knopf verbreitert seine Zeile = CLS). Nach dem Laden (Theme-Editor)
     oder ohne IntersectionObserver wie bisher sofort. */
  const DATA = document.getElementById('ap-glossar-daten');
  if (!DATA) return;

  /** Behälter, die annotiert werden dürfen. Alles andere bleibt unberührt. */
  const scopes = [...document.querySelectorAll('[data-ap-term-scope]')]
    .map((el) => el.getAttribute('data-ap-term-scope'))
    .filter(Boolean);
  if (!scopes.length) return;

  const VERBOTEN = 'h1,h2,h3,h4,h5,h6,a,button,label,code,pre,abbr,ap-term,script,style,select,textarea';
  const BLOCK_MS = 8;
  const NAH = '200px 0px';

  const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const jetzt = () => (window.performance && typeof performance.now === 'function' ? performance.now() : Date.now());
  const glossarUrl = DATA.getAttribute('data-glossar-url') || '/pages/reifenkennzeichnungen';

  /** Glossar lesen und die Einträge bauen — erst beim Start, nicht beim Auswerten der Datei. null = nichts zu tun. */
  function eintraegeBauen() {
    let glossar;
    try {
      glossar = JSON.parse(DATA.textContent);
    } catch {
      return null; // kaputte Nutzlast -> Text bleibt, wie er ist
    }
    if (!glossar || !Array.isArray(glossar.terms)) return null;
    // Ein Eintrag je Code, längster zuerst — sonst schlägt "MO" zu, bevor "MOE" drankommt.
    const liste = [];
    for (const t of glossar.terms) {
      for (const code of t.codes || []) {
        if (code.length < 2) continue; // Einzelzeichen wie "*" oder "J" wären Rauschen
        if (!/^[A-Z0-9][A-Z0-9.+&/-]*$/.test(code)) continue;
        liste.push({ code, label: t.label, slug: t.slug });
      }
    }
    liste.sort((a, b) => b.code.length - a.code.length);
    return liste.length ? liste : null;
  }

  /** Die Behälter in der bisherigen Reihenfolge: je Freigabe (scopes) ihre Treffer in Dokumentreihenfolge. */
  function wurzelnSammeln() {
    const out = [];
    for (const sel of scopes) {
      let treffer;
      try {
        treffer = document.querySelectorAll(sel);
      } catch {
        continue;
      }
      for (const w of treffer) out.push(w);
    }
    return out;
  }

  /** Die erlaubten Textknoten eines Behälters als Abschnitte { node, text, ab } in Dokumentreihenfolge. Verbotene
   *  Teilbäume (Überschrift, Link, Knopf, schon vorhandenes ap-term …) werden ganz übersprungen; die Vorfahren des
   *  Behälters prüft EIN closest() (vorher je Textknoten und je Code). Geplante, noch nicht eingesetzte Knöpfe
   *  (reserviert) gelten wie ein eingesetztes ap-term: ihr Text ist für weitere Codes verboten. */
  function abschnitte(wurzel, reserviert) {
    const out = [];
    if (!wurzel || !wurzel.isConnected || wurzel.closest(VERBOTEN)) return out;
    const lauf = document.createTreeWalker(wurzel, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (node.nodeType === 1) return node.matches(VERBOTEN) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_SKIP;
        return node.nodeValue ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      },
    });
    for (let n = lauf.nextNode(); n; n = lauf.nextNode()) {
      const spannen = reserviert && reserviert.get(n);
      if (!spannen) {
        out.push({ node: n, text: n.nodeValue, ab: 0 });
        continue;
      }
      let ab = 0;
      for (const s of spannen.slice().sort((a, b) => a.ab - b.ab)) {
        out.push({ node: n, text: n.nodeValue.slice(ab, s.ab), ab });
        ab = s.ab + s.len;
      }
      out.push({ node: n, text: n.nodeValue.slice(ab), ab });
    }
    return out;
  }

  /** Plant die Knöpfe EINES Behälters — dieselben Fundstellen wie bisher (siehe Kopf), nur ohne DOM-Arbeit. Liefert
   *  [{ node, ab, eintrag, id }]; teilt die Abschnitte wie splitText es täte. */
  function planen(wurzel, zustand) {
    const plan = [];
    const teile = abschnitte(wurzel, zustand.reserviert);
    if (!teile.length) return plan;
    const alles = teile.map((t) => t.text).join('\u0000');
    for (const eintrag of zustand.eintraege) {
      if (zustand.gesehen.has(eintrag.code)) continue;
      if (alles.indexOf(eintrag.code) === -1) continue; // Vorfilter: im Text dieses Behälters gar nicht vorhanden
      const re = new RegExp(`(^|[^A-Za-z0-9])(${esc(eintrag.code)})(?![A-Za-z0-9])`);
      for (let i = 0; i < teile.length; i++) {
        const t = teile[i];
        if (t.text.length < eintrag.code.length || t.text.indexOf(eintrag.code) === -1) continue;
        const m = re.exec(t.text);
        if (!m) continue;
        const start = m.index + m[1].length;
        zustand.lfd += 1;
        const punkt = { node: t.node, ab: t.ab + start, eintrag, id: `ApTerm-${zustand.lfd}` };
        plan.push(punkt);
        if (!zustand.reserviert.has(t.node)) zustand.reserviert.set(t.node, []);
        zustand.reserviert.get(t.node).push({ ab: punkt.ab, len: eintrag.code.length });
        // wie splitText: davor | Code (ab jetzt in einem ap-term, also verboten) | danach
        teile.splice(
          i,
          1,
          { node: t.node, text: t.text.slice(0, start), ab: t.ab },
          { node: t.node, text: t.text.slice(start + eintrag.code.length), ab: t.ab + start + eintrag.code.length },
        );
        zustand.gesehen.add(eintrag.code);
        break;
      }
    }
    return plan;
  }

  /** Das Element eines Knopfs — unverändert gegenüber vorher. */
  function knopf(eintrag, id) {
    const el = document.createElement('ap-term');
    el.className = 'ap-term';
    el.innerHTML =
      `<button type="button" class="ap-term__btn" aria-expanded="false" aria-controls="${id}">` +
      `${eintrag.code}<span class="ap-term__i" aria-hidden="true">i</span>` +
      `<span class="visually-hidden"> – Erklärung anzeigen</span></button>` +
      `<span class="ap-term__pop" id="${id}" hidden role="note"></span>`;
    // Text NIE per innerHTML einsetzen — er kommt aus einem Metafeld.
    const pop = el.querySelector('.ap-term__pop');
    pop.textContent = eintrag.label + ' ';
    const link = document.createElement('a');
    link.href = `${glossarUrl}#code-${eintrag.slug}`;
    link.textContent = 'Mehr im Kennungs-ABC';
    pop.appendChild(link);
    return el;
  }

  /** Setzt die Knöpfe EINES Textknotens ein — von hinten nach vorn, damit die Stellen davor gültig bleiben. Hat sich
   *  der Text seit der Planung geändert (neu gerendert), entfällt der Knopf. */
  function einsetzen(punkte) {
    punkte.sort((a, b) => b.ab - a.ab);
    for (const p of punkte) {
      const n = p.node;
      if (!n.isConnected || n.nodeValue.substr(p.ab, p.eintrag.code.length) !== p.eintrag.code) continue;
      const rest = n.splitText(p.ab);
      rest.splitText(p.eintrag.code.length);
      rest.parentNode.replaceChild(knopf(p.eintrag, p.id), rest);
    }
  }

  /** Alle geplanten Knöpfe je Textknoten gruppiert einsetzen; ihre Reservierung entfällt damit (der Text steht
   *  jetzt im ap-term). */
  function allesEinsetzen(plan, zustand) {
    const jeKnoten = new Map();
    for (const p of plan) {
      if (!jeKnoten.has(p.node)) jeKnoten.set(p.node, []);
      jeKnoten.get(p.node).push(p);
    }
    jeKnoten.forEach((punkte, node) => {
      zustand.reserviert.delete(node);
      einsetzen(punkte);
    });
  }

  /* ---- Sofort (nach dem Laden bzw. ohne IntersectionObserver): wie bisher, vollständig ---- */
  function sofort() {
    const liste = eintraegeBauen();
    if (!liste) return;
    const zustand = { eintraege: liste, gesehen: new Set(), lfd: 0, reserviert: new Map() };
    for (const w of wurzelnSammeln()) allesEinsetzen(planen(w, zustand), zustand);
  }

  /* ---- Spät: Planen in Blöcken, Einsetzen außerhalb des Bildes ---- */
  function spaet() {
    const wurzeln = wurzelnSammeln();
    if (!wurzeln.length) return;
    let gestartet = false;
    let nahBeob = null;

    // Knöpfe außerhalb des Bildes: je Absatz (Elternelement des Textknotens) ein Eintrag, eingesetzt, sobald der
    // IntersectionObserver ihn „nicht im Bild" meldet (beim ersten Melden oder beim Verlassen).
    const wartend = new Map();
    const sichtBeob = new IntersectionObserver((eintraege) => {
      for (const e of eintraege) {
        if (e.isIntersecting) continue;
        const punkte = wartend.get(e.target);
        wartend.delete(e.target);
        sichtBeob.unobserve(e.target);
        if (punkte) allesEinsetzen(punkte, zustand);
      }
    });
    function vormerken(plan) {
      for (const p of plan) {
        const absatz = p.node.parentElement;
        if (!absatz) continue;
        if (!wartend.has(absatz)) {
          wartend.set(absatz, []);
          sichtBeob.observe(absatz);
        }
        wartend.get(absatz).push(p);
      }
    }

    // Planen: Behälter nach Behälter in der bisherigen Reihenfolge, je Block höchstens BLOCK_MS.
    const zustand = { eintraege: null, gesehen: new Set(), lfd: 0, reserviert: new Map() };
    let naechste = 0;
    function block(frist) {
      const ab = jetzt();
      if (!zustand.eintraege) {
        zustand.eintraege = eintraegeBauen();
        if (!zustand.eintraege) return;
      }
      while (naechste < wurzeln.length) {
        vormerken(planen(wurzeln[naechste++], zustand));
        const verbraucht = jetzt() - ab;
        const rest = frist && typeof frist.timeRemaining === 'function' ? frist.timeRemaining() : BLOCK_MS - verbraucht;
        if (verbraucht >= BLOCK_MS || rest <= 1) break;
      }
      if (naechste < wurzeln.length) weiter();
    }
    function weiter() {
      if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(block, { timeout: 1000 });
      else window.setTimeout(block, 1);
    }
    function los() {
      if (gestartet) return;
      gestartet = true;
      if (nahBeob) nahBeob.disconnect();
      // Der erste Block läuft nicht im Rückruf des Beobachters, sondern als eigene Aufgabe (≤ 8 ms).
      window.setTimeout(block, 0);
    }

    // Start: ein Behälter kommt auf 200 px an das Bild heran — oder der Leerlauf nach dem Laden.
    nahBeob = new IntersectionObserver(
      (eintraege) => {
        if (eintraege.some((e) => e.isIntersecting)) los();
      },
      { rootMargin: NAH },
    );
    for (const w of wurzeln) nahBeob.observe(w);
    const leerlauf = () => {
      if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(los, { timeout: 3000 });
      else window.setTimeout(los, 200);
    };
    if (document.readyState === 'complete') leerlauf();
    else window.addEventListener('load', leerlauf, { once: true });
  }

  if (document.readyState === 'complete' || typeof window.IntersectionObserver !== 'function') {
    sofort();
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', spaet, { once: true });
  } else {
    spaet();
  }
})();
